import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { useCallback, useEffect, useMemo } from 'react';
import { useField } from 'react-final-form';

import { getReasonBlockId, REASONS_BLOCK_ID } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormReason } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormReason';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getErknmKnmReasonTypesSelectOptions,
  getKnmDataPlanId,
  getKnmDataCreated,
  getKnmFormRef,
  getKnmIsErknmStatusCommentsExist,
  getKnmIsNew,
  getKnmIsTypeErknmPlanned,
  getKnmPlanFindDataYear,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';
import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { IKnmReasonTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonTypeSelectOption';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ErknmApproveRemarkAddButton } from 'KnmErknmPackage/ErknmApproveRemarkModule/components/ErknmApproveRemarkAddButton';
import { getIsPlanErknmStatusConsidered } from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { useHasFieldApproveRemark } from 'KnmErknmPackage/MainModule/common/useHasFieldApproveRemark';

import { ErknmReasonItem } from './ErknmReasonsItem';
import { getPlanListPlanErknmStatus } from '../../../PlanListPackage/MainModule/selectors/planListSelectors';
import { ErknmPlanStatusesEnum } from '../../../APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';
import { ErknmApproveRemarkSectionCodeEnum } from '../../ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { ErknmApproveRemark } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemark';
import { ErknmApproveRemarkChanges } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemarkChanges';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import { ErknmApproveRemarkFieldCodeEnum } from '../../ErknmApproveRemarkModule/enums/ErknmApproveRemarkFieldCodeEnum';

interface IErknmReasonsProps {
  watchOnly: boolean;
  typeCode: string | undefined;
}

interface IErknmReasonsStoreProps {
  formRef: IKnmFormRef | undefined;
  erknmKnmReasonTypesSelectOptions: IKnmReasonTypeSelectOption[];
  isKnmStatusCommentsExist: boolean;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  planYear?: number;
  isPlanned: boolean;
  isNewKnm: boolean;
  created: string | undefined | null;
}

const ErknmReasonsWithoutStore = ({
  watchOnly,
  typeCode,
  erknmKnmReasonTypesSelectOptions,
  formRef,
  isKnmStatusCommentsExist,
  knmPlanStatus,
  planYear,
  isPlanned,
  isNewKnm,
  created,
}: IErknmReasonsProps & IErknmReasonsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const reasonsErknm = useField('reasonsErknm' as keyof IKnmForm, { subscription: { value: true } }).input.value;
  const firstReasonTitle = Array.isArray(reasonsErknm) ? reasonsErknm?.[0]?.title : undefined;
  const isKnmPlanStatusConsidered = getIsPlanErknmStatusConsidered(knmPlanStatus);
  const isKnmKindRemarkExist = useHasFieldApproveRemark(ErknmApproveRemarkSectionCodeEnum.KIND_KNM);

  const fieldBlockLabel =
    typeCode === KnmTypeCodesEnum.VP ? 'Основание проведения мероприятия' : 'Основание включения в план';

  const isReasonEditDisable =
    isPlanned &&
    planYear != null &&
    planYear <= new Date().getFullYear() &&
    isKnmStatusCommentsExist &&
    knmPlanStatus === ErknmPlanStatusesEnum.CONSIDERED;

  const isDisabled = (): boolean => {
    const reasonsFieldLength = formRef?.current.getFieldState('reasonsErknm')?.value?.length;

    return reasonsFieldLength ? reasonsFieldLength > 0 : false;
  };

  const addReason = useCallback(() => {
    if (!watchOnly && push) {
      const addingReason: IKnmErknmFormReason = {
        type: undefined,
        text: undefined,
        date: undefined,
        riskIndicators: undefined,
        requirementDetails: undefined,
        dateRequirement: undefined,
        assignmentNumber: undefined,
        assignmentDate: undefined,
        dateBegin: undefined,
        dateEnd: undefined,
        links: undefined,
        inspectionResults: undefined,
        ervkUuid: undefined,
        listId: uuidv4(),
      };

      push('reasonsErknm', addingReason);
    }
  }, [push, watchOnly]);

  const validIds = useMemo(() => {
    if (!Array.isArray(erknmKnmReasonTypesSelectOptions)) return new Set();

    return new Set(erknmKnmReasonTypesSelectOptions.map(option => option.ervkUuid));
  }, [erknmKnmReasonTypesSelectOptions]);

  useEffect(() => {
    if (!isKnmPlanStatusConsidered || !isKnmKindRemarkExist) {
      return;
    }

    if (!formRef?.current || !Array.isArray(reasonsErknm) || reasonsErknm.length === 0 || validIds.size === 0) {
      return;
    }

    const cleared = reasonsErknm.map(reason => {
      const typeId = reason?.type?.ervkUuid || reason?.type?.id;

      const isInvalid = typeId && !validIds.has(typeId);

      return isInvalid ? { ...reason, type: undefined } : reason;
    });

    if (!cleared.every((r, i) => r === reasonsErknm[i])) {
      formRef.current.change('reasonsErknm', cleared);
    }
  }, [validIds, formRef, reasonsErknm]);

  return (
    <FieldBlock
      label={
        <div className="shared-inline-flex">
          {fieldBlockLabel}
          <ErknmFieldMark fieldName="reasonsErknm" />
          <ErknmApproveRemarkChanges
            sectionCode={ErknmApproveRemarkSectionCodeEnum.REASONS}
            fieldCode={ErknmApproveRemarkFieldCodeEnum.REASON}
            oldValue={firstReasonTitle}
          />
        </div>
      }
      id={REASONS_BLOCK_ID}
      button={
        <>
          <Button
            className="shared-btn-add"
            text="Добавить"
            variant="link"
            color="primary"
            type="button"
            id="addReasonButton"
            disabled={watchOnly || isDisabled() || isReasonEditDisable}
            onClick={addReason}
          />
          <ErknmApproveRemarkAddButton sectionCode={ErknmApproveRemarkSectionCodeEnum.REASONS} />
        </>
      }
      fillInProcess={<ErknmFillInProcessMessage name="reasonsErknm" />}
      className="shared-field"
    >
      <ErknmApproveRemark sectionCode={ErknmApproveRemarkSectionCodeEnum.REASONS} />
      <FieldArray name="reasonsErknm">
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormReason, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }
          const reasonsFieldsValue: IKnmErknmFormReason[] = fields.value;

          return (
            <Row>
              {fields.map((reasonsFieldName, sIndex) => {
                const reasonFieldValue: IKnmErknmFormReason = fields.value?.[sIndex];
                const id = getReasonBlockId(reasonFieldValue.listId);
                const typesSelectOptions:
                  | IKnmReasonTypeSelectOption[]
                  | undefined = erknmKnmReasonTypesSelectOptions?.map(selectOption => ({
                  ...selectOption,
                  isDisabled: reasonsFieldsValue?.some(
                    reasonsFieldsValueItem =>
                      reasonsFieldsValueItem.type?.id === selectOption.id &&
                      selectOption.id !== reasonFieldValue.type?.id,
                  ),
                }));

                return (
                  <Row key={id} id={id} margin="medium" isListItem>
                    <ErknmReasonItem
                      watchOnly={watchOnly || isReasonEditDisable}
                      reasonFieldValue={reasonFieldValue}
                      reasonsFieldName={reasonsFieldName}
                      sIndex={sIndex}
                      typesSelectOptions={typesSelectOptions}
                      formRef={formRef}
                      isNewKnm={isNewKnm}
                      created={created}
                    />
                  </Row>
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmReasonsStoreProps => ({
  formRef: getKnmFormRef(state),
  isNewKnm: getKnmIsNew(state),
  erknmKnmReasonTypesSelectOptions: getErknmKnmReasonTypesSelectOptions(state),
  isKnmStatusCommentsExist: getKnmIsErknmStatusCommentsExist(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  planYear: getKnmPlanFindDataYear(state),
  isPlanned: getKnmIsTypeErknmPlanned(state) || !!getKnmDataPlanId(state),
  created: getKnmDataCreated(state),
});

export const ErknmReasons = connect(mapStateToProps)(ErknmReasonsWithoutStore);
